/**
 * `parsePhiraColor` (W1-08 formats-legacy-respack; contract formats/respack/types.ts; skin-and-assets.md S§2.2):
 * respack colours are u32. A value above 0xFFFFFF is ARGB (alpha in the top byte); anything else is RGB with
 * alpha forced to 1 - so an ARGB colour whose alpha byte is 0 is indistinguishable from RGB and becomes opaque.
 * Channels are returned as f32 values in 0..1 (byte / 255), as Phira's colour type stores them.
 */

import type { ParsePhiraColorFn } from './types';

const fr = Math.fround;
const U32 = 2 ** 32;

/** The u32 bit pattern of a number (truncated, wrapped like a Rust `as u32` of an integer; NaN -> 0). */
export function toU32(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const t = Math.trunc(v);
  return ((t % U32) + U32) % U32;
}

export const parsePhiraColor: ParsePhiraColorFn = (v) => {
  const u = toU32(v);
  const r = Math.floor(u / 0x10000) % 256;
  const g = Math.floor(u / 0x100) % 256;
  const b = u % 256;
  const a = u > 0xffffff ? Math.floor(u / 0x1000000) : 255;
  return [fr(r / 255), fr(g / 255), fr(b / 255), fr(a / 255)];
};

/** "0xAARRGGBB" (or "0xRRGGBB" for values Phira reads as RGB), the form respack info.yml files use. */
export function formatPhiraColor(v: number): string {
  const u = toU32(v);
  const hex = u.toString(16).toUpperCase();
  return `0x${hex.padStart(u > 0xffffff ? 8 : 6, '0')}`;
}
